import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import SplitText from "gsap/SplitText"
import { fresponsive } from "library/fullyResponsive"
import useAnimation from "library/useAnimation"
import type { ReactNode } from "react"
import { useRef } from "react"
import styled, { css } from "styled-components"

gsap.registerPlugin(SplitText)

export default function TextAnimation({
	children,
	delay = 0,
	multiLine = false,
}: {
	children: ReactNode
	delay?: number
	multiLine?: boolean
}) {
	const wrapperRef = useRef<HTMLDivElement | null>(null)

	useAnimation(
		() => {
			if (!wrapperRef.current) return

			const multiLineAnimate = () => {
				const textSplit = new SplitText(wrapperRef.current, {
					type: "lines",
					linesClass: "line-inner",
					position: "relative",
				})
				// used to give each line a hidden overflow
				new SplitText(wrapperRef.current, {
					type: "lines",
					linesClass: "line-wrapper",
					position: "relative",
				})

				const tl = gsap.timeline({ paused: true, delay })
				tl.set(wrapperRef.current, {
					opacity: 1,
				})

				tl.to(textSplit.lines, {
					y: 0,
					stagger: 0.25,
				})

				return tl
			}

			const singleAnimate = () => {
				const tl = gsap.timeline({ paused: true, delay })
				tl.to(wrapperRef.current, {
					opacity: 1,
				})

				return tl
			}

			const animate = multiLine ? multiLineAnimate() : singleAnimate()

			const initScrollTrigger = () => {
				ScrollTrigger.create({
					trigger: wrapperRef.current,
					start: "top 75%",
					onEnter: () => {
						animate.play()
					},
				})
			}

			initScrollTrigger()
		},
		[delay, multiLine],
		{ scope: wrapperRef },
	)

	return <Wrapper ref={wrapperRef}>{children}</Wrapper>
}

const Wrapper = styled.div`
  opacity: 0;
  overflow: clip;

  .line-wrapper {
    overflow: clip;

    ${fresponsive(css`
      margin-bottom: -0.1em;
      padding-bottom: 0.1em;
    `)}
  }

  .line-inner {
    transform: translateY(calc(100% + 0.1em));

    ${fresponsive(css`
      padding-bottom: 9px;
    `)}
  }
`
